import React, { useState, useEffect, useCallback } from 'react';
import './GastosData.css';
import baseURL from '../../url';
import moneda from '../../moneda';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faReceipt,
    faPlus,
    faTrash,
    faSearch,
    faFilter,
    faCalendarAlt,
    faSync,
    faCoins,
    faTags,
    faChartPie
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NewGastoModal from './NewGastoModal';
import NewCategoriaGastoModal from './NewCategoriaGastoModal';

export default function GastosData() {
    const [gastos, setGastos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filtros
    const [busqueda, setBusqueda] = useState('');
    const [categoriaFiltro, setCategoriaFiltro] = useState('todas');
    const [desde, setDesde] = useState('');
    const [hasta, setHasta] = useState('');

    // Modales
    const [modalGastoOpen, setModalGastoOpen] = useState(false);
    const [modalCatOpen, setModalCatOpen] = useState(false);

    const cargarCategorias = async () => {
        try {
            const res = await fetch(`${baseURL}/categoriaGastoGet.php`);
            const data = await res.json();
            setCategorias(data.categorias || []);
        } catch (e) {
            console.error('Error al cargar categorías de gastos:', e);
        }
    };

    const cargarGastos = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${baseURL}/gastoGet.php?`;
            const params = [];
            if (desde) params.push(`desde=${desde}`);
            if (hasta) params.push(`hasta=${hasta}`);
            if (categoriaFiltro !== 'todas') params.push(`categoria=${encodeURIComponent(categoriaFiltro)}`);
            url += params.join('&');

            const res = await fetch(url);
            const data = await res.json();
            setGastos(data.gastos || []);
        } catch (e) {
            console.error('Error al cargar gastos:', e);
            toast.error('Error al cargar gastos');
        } finally {
            setLoading(false);
        }
    }, [desde, hasta, categoriaFiltro]);

    useEffect(() => {
        cargarCategorias();
        cargarGastos();
    }, [cargarGastos]);

    const eliminarGasto = (idGasto, descripcion, monto) => {
        Swal.fire({
            title: '¿Eliminar este gasto?',
            text: `Se eliminará "${descripcion}" por ${moneda} ${monto}. ¡Esta acción no se puede revertir!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await fetch(`${baseURL}/gastoDelete.php?idGasto=${idGasto}`, {
                        method: 'DELETE'
                    });
                    const data = await res.json();
                    if (data.mensaje) {
                        Swal.fire('¡Eliminado!', data.mensaje, 'success');
                        cargarGastos();
                    } else {
                        toast.error(data.error || 'No se pudo eliminar');
                    }
                } catch (e) {
                    toast.error('Error de red al eliminar gasto');
                }
            }
        });
    };

    // Filtrado por texto (búsqueda)
    const gastosFiltrados = gastos.filter(g => {
        const texto = `${g.descripcion} ${g.categoria} ${g.nota || ''}`.toLowerCase();
        return texto.includes(busqueda.toLowerCase());
    });

    const totalFiltrado = gastosFiltrados.reduce((acc, g) => acc + parseFloat(g.monto || 0), 0);
    const promedioGasto = gastosFiltrados.length > 0 ? totalFiltrado / gastosFiltrados.length : 0;

    return (
        <div className="gastosContainer">
            <ToastContainer />

            {/* Cabecera y Resumen de Totales */}
            <div className="gastosHeader">
                <div className="gastosTitleCol">
                    <h2><FontAwesomeIcon icon={faReceipt} /> Gestión de Gastos Operativos</h2>
                    <p>Registra y clasifica los costos operativos, servicios, insumos y compras del restaurante.</p>
                </div>

                <div className="gastosTopButtons">
                    <button className="btnNuevaCategoria" onClick={() => setModalCatOpen(true)}>
                        <FontAwesomeIcon icon={faTags} /> Administrar Categorías
                    </button>
                    <button className="btnNuevoGastoPrincipal" onClick={() => setModalGastoOpen(true)}>
                        <FontAwesomeIcon icon={faPlus} /> Registrar Gasto
                    </button>
                </div>
            </div>

            {/* Tarjetas de Resumen Rápido */}
            <div className="gastosResumenCards">
                <div className="resumenCard">
                    <div className="resumenCardIcon bg-red-soft">
                        <FontAwesomeIcon icon={faCoins} />
                    </div>
                    <div className="resumenCardText">
                        <span>Total de Gastos</span>
                        <strong>{moneda} {totalFiltrado.toFixed(2)}</strong>
                    </div>
                </div>

                <div className="resumenCard">
                    <div className="resumenCardIcon bg-blue-soft">
                        <FontAwesomeIcon icon={faReceipt} />
                    </div>
                    <div className="resumenCardText">
                        <span>Nº de Registros</span>
                        <strong>{gastosFiltrados.length} gastos</strong>
                    </div>
                </div>

                <div className="resumenCard">
                    <div className="resumenCardIcon bg-purple-soft">
                        <FontAwesomeIcon icon={faChartPie} />
                    </div>
                    <div className="resumenCardText">
                        <span>Gasto Promedio</span>
                        <strong>{moneda} {promedioGasto.toFixed(2)}</strong>
                    </div>
                </div>
            </div>

            {/* Barra de Filtros y Búsqueda */}
            <div className="gastosToolbar">
                <div className="gastosSearchBox">
                    <FontAwesomeIcon icon={faSearch} />
                    <input
                        type="text"
                        placeholder="Buscar por concepto, categoría o nota..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                    />
                </div>

                <div className="gastosFiltersRow">
                    <div className="filterGroup">
                        <label><FontAwesomeIcon icon={faFilter} /> Categoría:</label>
                        <select
                            value={categoriaFiltro}
                            onChange={(e) => setCategoriaFiltro(e.target.value)}
                            className="selectFiltroCat"
                        >
                            <option value="todas">Todas las categorías</option>
                            {categorias.map(c => (
                                <option key={c.idCategoriaGasto} value={c.categoria}>{c.categoria}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filterGroup">
                        <label><FontAwesomeIcon icon={faCalendarAlt} /> Desde:</label>
                        <input
                            type="date"
                            value={desde}
                            onChange={(e) => setDesde(e.target.value)}
                        />
                    </div>

                    <div className="filterGroup">
                        <label><FontAwesomeIcon icon={faCalendarAlt} /> Hasta:</label>
                        <input
                            type="date"
                            value={hasta}
                            onChange={(e) => setHasta(e.target.value)}
                        />
                    </div>

                    <button className="btnReloadGastos" onClick={cargarGastos} title="Recargar">
                        <FontAwesomeIcon icon={faSync} className={loading ? 'fa-spin' : ''} />
                    </button>
                </div>
            </div>

            {/* Tabla de Gastos */}
            <div className="gastosTableWrapper">
                {loading ? (
                    <div className="gastosLoading">Cargando registros de gastos...</div>
                ) : gastosFiltrados.length === 0 ? (
                    <div className="noGastosFound">No se encontraron gastos con los filtros aplicados.</div>
                ) : (
                    <table className="gastosTable">
                        <thead>
                            <tr>
                                <th># ID</th>
                                <th>Fecha</th>
                                <th>Concepto / Descripción</th>
                                <th>Categoría</th>
                                <th>Monto</th>
                                <th>Observaciones</th>
                                <th style={{ textAlign: 'center' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {gastosFiltrados.map(g => (
                                <tr key={g.idGasto}>
                                    <td><strong>#{g.idGasto}</strong></td>
                                    <td className="colGastoFecha">{g.fecha}</td>
                                    <td>
                                        <strong className="gastoConcepto">{g.descripcion}</strong>
                                    </td>
                                    <td>
                                        <span className="gastoCatBadge">{g.categoria}</span>
                                    </td>
                                    <td className="colGastoMonto">
                                        <strong>{moneda} {parseFloat(g.monto).toFixed(2)}</strong>
                                    </td>
                                    <td className="colGastoNota">
                                        {g.nota || <span className="textMuted">-</span>}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <button
                                            className="btnDeleteGasto"
                                            onClick={() => eliminarGasto(g.idGasto, g.descripcion, g.monto)}
                                            title="Eliminar gasto"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modales */}
            <NewGastoModal
                isOpen={modalGastoOpen}
                onClose={() => setModalGastoOpen(false)}
                onGastoCreado={cargarGastos}
                onAbrirNuevaCategoria={() => setModalCatOpen(true)}
            />

            <NewCategoriaGastoModal
                isOpen={modalCatOpen}
                onClose={() => setModalCatOpen(false)}
                onCategoriaCreada={() => { cargarCategorias(); cargarGastos(); }}
            />
        </div>
    );
}
